import React, { useState, useEffect, useRef } from 'react';
import {
  Edit2, Save, X, RefreshCw, Printer,
  Building2, Calendar, DollarSign, Plus, Trash2, MapPin
} from 'lucide-react';
import apiClient from '../services/api';

const NAVY       = '#0d1c6b';
const NAVY_LIGHT = '#1a3a8f';

function getSessionCompanyId() {
  try {
    const raw = localStorage.getItem('ltms_session');
    if (raw) {
      const parsed = JSON.parse(raw);
      const id = parsed?.session?.companyid ?? parsed?.companyid;
      if (id) return id;
    }
  } catch { /* ignore */ }
  return sessionStorage.getItem('companyid') || 1;
}

const GeneralSetup = () => {
  const [loading,       setLoading]       = useState(true);
  const [editMode,      setEditMode]      = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [errors,        setErrors]        = useState({});

  const [companyData,   setCompanyData]   = useState({
    companyid: null, companycode: '', companyname: '', companynamearabic: '',
    address: '', city: '', state: '', country: '', postal_code: '',
    phone: '', fax: '', email: '', website: '',
    taxnumber: '', registrationnumber: '', logopath: '',
    isactive: 1, IsDefault: 0,
  });
  const [originalData,         setOriginalData]         = useState(null);
  const [countries,            setCountries]            = useState([]);
  const [cities,               setCities]               = useState([]);
  const [filteredCities,       setFilteredCities]       = useState([]);
  const [currencies,           setCurrencies]           = useState([]);
  const [baseCurrency,         setBaseCurrency]         = useState(null);
  const [financialYearInfo,    setFinancialYearInfo]    = useState('Loading financial year information…');
  const [exchangeRates,        setExchangeRates]        = useState([]);
  const [originalExchangeRates,setOriginalExchangeRates]= useState([]);

  const companyNameRef = useRef(null);

  useEffect(() => { loadAllData(); }, []);

  useEffect(() => {
    if (!companyData.country) { setFilteredCities(cities); return; }
    const f = cities.filter(c =>
      (c.countryname && c.countryname === companyData.country) ||
      (c.countryid   && String(c.countryid) === String(companyData.country))
    );
    setFilteredCities(f.length ? f : cities);
  }, [companyData.country, cities]);

  const loadAllData = async () => {
    setLoading(true);
    try {
      await Promise.all([loadCountries(), loadCities(), loadCurrencies(), loadFinancialYear()]);
      await loadCompanyData();
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const loadCountries = async () => {
    try {
      const res = await apiClient.get('/Countries.php', { params: { active: true } });
      if (res.data.success) setCountries(res.data.data || []);
    } catch (e) { console.error(e); }
  };

  const loadCities = async () => {
    try {
      const res = await apiClient.get('/Cities.php', { params: { active: true } });
      if (res.data.success) { setCities(res.data.data || []); setFilteredCities(res.data.data || []); }
    } catch (e) { console.error(e); }
  };

  const loadCurrencies = async () => {
    try {
      const res = await apiClient.get('/Currencies.php', { params: { active: true } });
      if (res.data.success) {
        const list = res.data.data || [];
        setCurrencies(list);
        const base = list.find(c => c.statusbasecurrency === 1);
        if (base) { setBaseCurrency(base); loadExchangeRates(base.currencyid); }
      }
    } catch (e) { console.error(e); }
  };

  const loadCompanyData = async () => {
    try {
      const res = await apiClient.get('/Companies.php', { params: { id: getSessionCompanyId() } });
      if (res.data.success && res.data.data) {
        const c = res.data.data;
        const mapped = {
          companyid: c.companyid, companycode: c.companycode || '',
          companyname: c.companyname || '', companynamearabic: c.companynamearabic || '',
          address: c.address || '', city: c.city || '', state: c.state || '',
          country: c.country || '', postal_code: c.postal_code || '',
          phone: c.phone || '', fax: c.fax || '', email: c.email || '',
          website: c.website || '', taxnumber: c.taxnumber || '',
          registrationnumber: c.registrationnumber || '', logopath: c.logopath || '',
          isactive: c.isactive ?? 1, IsDefault: c.IsDefault ?? 0,
        };
        setCompanyData(mapped); setOriginalData({ ...mapped });
      }
    } catch (e) { console.error(e); }
  };

  const loadFinancialYear = async () => {
    try {
      const res = await apiClient.get('/FinancialYears.php', { params: { companyid: getSessionCompanyId(), current: true } });
      if (res.data.success && res.data.data) {
        const fy = Array.isArray(res.data.data) ? res.data.data[0] : res.data.data;
        if (fy) {
          const from = new Date(fy.startdate).toLocaleDateString('en-GB');
          const to   = new Date(fy.enddate).toLocaleDateString('en-GB');
          setFinancialYearInfo(`Current Financial Year: ${fy.yearname} (${from} to ${to}) — Status: ${fy.isclosed ? 'Closed' : 'Open'}`);
          return;
        }
      }
      setFinancialYearInfo('No current financial year information available');
    } catch { setFinancialYearInfo('Error loading financial year information'); }
  };

  const loadExchangeRates = async (baseCurrencyId) => {
    if (!baseCurrencyId) { setExchangeRates([]); setOriginalExchangeRates([]); return; }
    try {
      const res = await apiClient.get('/ExchangeRates.php', { params: { tocurrencyid: baseCurrencyId } });
      if (res.data.success) { const r = res.data.data || []; setExchangeRates(r); setOriginalExchangeRates(JSON.parse(JSON.stringify(r))); }
    } catch (e) { console.error(e); }
  };

  const handleInputChange = (field, value) => {
    if (field === 'country') setCompanyData(p => ({ ...p, country: value, city: '' }));
    else setCompanyData(p => ({ ...p, [field]: value }));
    if (errors[field]) setErrors(p => ({ ...p, [field]: null }));
  };

  const addExchangeRateRow = () => {
    if (!baseCurrency) { alert('Please set a base currency first in the Currencies master.'); return; }
    setExchangeRates(p => [...p, { exchangerateid: null, fromcurrencyid: '', rate: '', effectivedate: new Date().toISOString().split('T')[0], tocurrencyid: baseCurrency.currencyid, isNew: true }]);
  };

  const removeExchangeRateRow = (index) => {
    if (window.confirm('Remove this exchange rate?')) setExchangeRates(p => p.filter((_, i) => i !== index));
  };

  const updateExchangeRate = (index, field, value) => {
    setExchangeRates(p => { const n = [...p]; n[index] = { ...n[index], [field]: value }; return n; });
  };

  const validate = () => {
    const e = {};
    if (!companyData.companyname?.trim()) e.companyname = 'Company name is required';
    if (!companyData.companycode?.trim()) e.companycode = 'Company code is required';
    if (companyData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(companyData.email)) e.email = 'Invalid email format';
    exchangeRates.forEach((r, i) => {
      if (!r.fromcurrencyid) e[`rate_currency_${i}`] = 'Currency required';
      if (!r.rate || parseFloat(r.rate) <= 0) e[`rate_value_${i}`] = 'Valid rate required';
      if (!r.effectivedate) e[`rate_date_${i}`] = 'Date required';
    });
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleEdit = () => {
    setEditMode(true);
    setOriginalData(JSON.parse(JSON.stringify(companyData)));
    setOriginalExchangeRates(JSON.parse(JSON.stringify(exchangeRates)));
    setTimeout(() => companyNameRef.current?.focus(), 100);
  };

  const handleSave = async () => {
    if (!validate()) return;
    setSubmitLoading(true);
    try {
      const compRes = await apiClient.put('/Companies.php', companyData);
      if (!compRes.data.success) throw new Error(compRes.data.message || 'Failed to save company data');
      if (baseCurrency) {
        for (const rate of exchangeRates) {
          if (rate.isNew || !rate.exchangerateid) {
            await apiClient.post('/ExchangeRates.php', { fromcurrencyid: rate.fromcurrencyid, tocurrencyid: baseCurrency.currencyid, rate: parseFloat(rate.rate), effectivedate: rate.effectivedate });
          } else {
            await apiClient.put('/ExchangeRates.php', { exchangerateid: rate.exchangerateid, fromcurrencyid: rate.fromcurrencyid, tocurrencyid: baseCurrency.currencyid, rate: parseFloat(rate.rate), effectivedate: rate.effectivedate });
          }
        }
        for (const orig of originalExchangeRates) {
          if (!exchangeRates.find(r => r.exchangerateid === orig.exchangerateid) && orig.exchangerateid)
            await apiClient.delete('/ExchangeRates.php', { data: { exchangerateid: orig.exchangerateid } });
        }
      }
      alert('Company setup saved successfully!');
      setEditMode(false); loadAllData();
    } catch (err) { alert('Error saving: ' + (err.response?.data?.message || err.message)); }
    finally { setSubmitLoading(false); }
  };

  const handleCancel = () => {
    if (window.confirm('Discard all changes?')) { setCompanyData(originalData); setExchangeRates(originalExchangeRates); setEditMode(false); setErrors({}); }
  };

  const getCurrencyName = (id) => {
    const c = currencies.find(c => c.currencyid === parseInt(id));
    return c ? `${c.currencyname} (${c.symbol})` : '';
  };

  // Shared field class builders
  const inputCls = (err) =>
    `w-full px-3.5 py-2.5 border-2 rounded-lg text-sm font-medium outline-none transition-all duration-200
    ${editMode ? 'bg-white' : 'bg-gray-50 cursor-default'}
    ${err ? 'border-red-500 bg-red-50' : 'border-gray-200 focus:border-[#0d1c6b] focus:shadow-[0_0_0_3px_rgba(13,28,107,0.1)]'}
    text-gray-800`;

  const selectCls = (err) =>
    `w-full px-3.5 py-2.5 border-2 rounded-lg text-sm font-medium outline-none transition-all duration-200 bg-white
    ${err ? 'border-red-500 bg-red-50' : 'border-gray-200 focus:border-[#0d1c6b] focus:shadow-[0_0_0_3px_rgba(13,28,107,0.1)]'}
    text-gray-800`;

  const labelCls = 'block mb-1.5 text-[11px] font-bold text-gray-500 uppercase tracking-widest';
  const errMsg   = (msg) => msg ? <p className="text-[11px] text-red-600 font-semibold mt-1">⚠ {msg}</p> : null;

  const SectionCard = ({ title, icon: Icon, color = NAVY, children, extra }) => (
    <div className="bg-white border border-gray-200 rounded-xl p-6 mb-5 shadow-sm">
      <div className="flex items-center justify-between pb-3 mb-5 border-b border-gray-100">
        <div className="flex items-center gap-2 text-[15px] font-bold" style={{ color }}>
          <Icon size={17} /> {title}
        </div>
        {extra}
      </div>
      {children}
    </div>
  );

  const FieldRow = ({ children }) => (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4 last:mb-0">{children}</div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-gray-400 text-lg font-semibold">Loading General Setup…</div>
      </div>
    );
  }

  return (
    <div className="p-5 pb-16 bg-gray-50 min-h-screen print:bg-white print:p-0">

      {/* ── Toolbar ── */}
      <div className="flex items-center justify-between bg-white px-5 py-3.5 rounded-xl shadow-sm mb-5 flex-wrap gap-3 print:hidden">
        <div>
          <h2 className="flex items-center gap-2 text-[22px] font-extrabold m-0" style={{ color: NAVY }}>
            <Building2 size={22} style={{ color: NAVY }} />
            General Setup
          </h2>
          <p className="text-[13px] text-gray-500 mt-0.5 m-0">Company Configuration &amp; Exchange Rates</p>
        </div>
        <div className="flex gap-2">
          {!editMode ? (
            <>
              <button onClick={handleEdit}
                className="flex items-center gap-2 px-4 py-2.5 text-white text-sm font-bold border-0 rounded-lg cursor-pointer transition-all duration-150 hover:-translate-y-0.5"
                style={{ background: `linear-gradient(135deg, ${NAVY} 0%, ${NAVY_LIGHT} 100%)`, color: '#fff' }}>
                <Edit2 size={15} /> Edit
              </button>
              <button onClick={loadAllData}
                className="flex items-center gap-2 px-4 py-2.5 text-sm font-bold text-gray-700 bg-white border-2 border-gray-200 rounded-lg cursor-pointer transition-all duration-150 hover:bg-gray-50">
                <RefreshCw size={15} /> Refresh
              </button>
              <button onClick={() => window.print()}
                className="flex items-center gap-2 px-4 py-2.5 text-sm font-bold text-gray-700 bg-white border-2 border-gray-200 rounded-lg cursor-pointer transition-all duration-150 hover:bg-gray-50">
                <Printer size={15} /> Print
              </button>
            </>
          ) : (
            <>
              <button onClick={handleSave} disabled={submitLoading}
                className="flex items-center gap-2 px-4 py-2.5 text-white text-sm font-bold border-0 rounded-lg cursor-pointer transition-all duration-150 hover:-translate-y-0.5 disabled:opacity-60 disabled:cursor-not-allowed"
                style={{ background: `linear-gradient(135deg, ${NAVY} 0%, ${NAVY_LIGHT} 100%)`, color: '#fff' }}>
                <Save size={15} /> {submitLoading ? 'Saving…' : 'Save'}
              </button>
              <button onClick={handleCancel} disabled={submitLoading}
                className="flex items-center gap-2 px-4 py-2.5 text-sm font-bold text-gray-700 bg-white border-2 border-gray-200 rounded-lg cursor-pointer transition-all duration-150 hover:bg-gray-50 disabled:opacity-60">
                <X size={15} /> Cancel
              </button>
            </>
          )}
        </div>
      </div>

      <div className="max-w-[1080px] mx-auto">

        {/* ── 1. Company Details ── */}
        <SectionCard title="Company Details" icon={Building2} color={NAVY}>
          <FieldRow>
            <div>
              <label className={labelCls}>Company Code <span className="text-red-500">*</span></label>
              <input value={companyData.companycode} onChange={e => handleInputChange('companycode', e.target.value)}
                readOnly={!editMode} maxLength={20} className={inputCls(errors.companycode)} />
              {errMsg(errors.companycode)}
            </div>
            <div>
              <label className={labelCls}>Company Name <span className="text-red-500">*</span></label>
              <input ref={companyNameRef} value={companyData.companyname}
                onChange={e => handleInputChange('companyname', e.target.value)}
                readOnly={!editMode} maxLength={200} className={inputCls(errors.companyname)} />
              {errMsg(errors.companyname)}
            </div>
          </FieldRow>
          <FieldRow>
            <div>
              <label className={labelCls}>Company Name (Arabic)</label>
              <input value={companyData.companynamearabic} onChange={e => handleInputChange('companynamearabic', e.target.value)}
                readOnly={!editMode} maxLength={200} dir="rtl" className={inputCls(false)} />
            </div>
            <div>
              <label className={labelCls}>Address</label>
              <input value={companyData.address} onChange={e => handleInputChange('address', e.target.value)}
                readOnly={!editMode} maxLength={500} className={inputCls(false)} />
            </div>
          </FieldRow>
          <FieldRow>
            <div>
              <label className={labelCls}><MapPin size={11} className="inline mr-1" />Country</label>
              {editMode ? (
                <select value={companyData.country} onChange={e => handleInputChange('country', e.target.value)} className={selectCls(false)}>
                  <option value="">-- Select Country --</option>
                  {countries.map(c => <option key={c.countryid} value={c.countryname}>{c.countryname}</option>)}
                </select>
              ) : (
                <input value={companyData.country} readOnly className={inputCls(false)} />
              )}
            </div>
            <div>
              <label className={labelCls}><MapPin size={11} className="inline mr-1" />City</label>
              {editMode ? (
                <select value={companyData.city} onChange={e => handleInputChange('city', e.target.value)} className={selectCls(false)}>
                  <option value="">-- Select City --</option>
                  {filteredCities.map(c => <option key={c.cityid} value={c.cityname}>{c.cityname}</option>)}
                </select>
              ) : (
                <input value={companyData.city} readOnly className={inputCls(false)} />
              )}
            </div>
          </FieldRow>
          <FieldRow>
            <div>
              <label className={labelCls}>State</label>
              <input value={companyData.state} onChange={e => handleInputChange('state', e.target.value)} readOnly={!editMode} maxLength={100} className={inputCls(false)} />
            </div>
            <div>
              <label className={labelCls}>Postal Code</label>
              <input value={companyData.postal_code} onChange={e => handleInputChange('postal_code', e.target.value)} readOnly={!editMode} maxLength={20} className={inputCls(false)} />
            </div>
          </FieldRow>
          <FieldRow>
            <div>
              <label className={labelCls}>Phone</label>
              <input value={companyData.phone} onChange={e => handleInputChange('phone', e.target.value)} readOnly={!editMode} maxLength={50} className={inputCls(false)} />
            </div>
            <div>
              <label className={labelCls}>Fax</label>
              <input value={companyData.fax} onChange={e => handleInputChange('fax', e.target.value)} readOnly={!editMode} maxLength={50} className={inputCls(false)} />
            </div>
          </FieldRow>
          <FieldRow>
            <div>
              <label className={labelCls}>Email</label>
              <input type="email" value={companyData.email} onChange={e => handleInputChange('email', e.target.value)} readOnly={!editMode} maxLength={100} className={inputCls(errors.email)} />
              {errMsg(errors.email)}
            </div>
            <div>
              <label className={labelCls}>Website</label>
              <input value={companyData.website} onChange={e => handleInputChange('website', e.target.value)} readOnly={!editMode} maxLength={200} className={inputCls(false)} />
            </div>
          </FieldRow>
          <FieldRow>
            <div>
              <label className={labelCls}>Tax Number</label>
              <input value={companyData.taxnumber} onChange={e => handleInputChange('taxnumber', e.target.value)} readOnly={!editMode} maxLength={50} className={inputCls(false)} />
            </div>
            <div>
              <label className={labelCls}>Registration Number</label>
              <input value={companyData.registrationnumber} onChange={e => handleInputChange('registrationnumber', e.target.value)} readOnly={!editMode} maxLength={50} className={inputCls(false)} />
            </div>
          </FieldRow>
        </SectionCard>

        {/* ── 2. Base Currency ── */}
        {baseCurrency && (
          <SectionCard title="Base Currency" icon={DollarSign} color="#059669">
            <div className="px-4 py-3 bg-emerald-50 border border-emerald-200 rounded-lg text-emerald-700 font-semibold text-sm">
              {baseCurrency.currencyname} ({baseCurrency.symbol}) — Set in Currencies Master
            </div>
          </SectionCard>
        )}

        {/* ── 3. Financial Year ── */}
        <SectionCard title="Current Financial Year" icon={Calendar} color="#1d4ed8">
          <div className="px-4 py-3 bg-blue-50 border border-blue-200 rounded-lg text-blue-800 font-semibold text-sm">
            {financialYearInfo}
          </div>
        </SectionCard>

        {/* ── 4. Exchange Rates ── */}
        <SectionCard title="Exchange Rates (Against Base Currency)" icon={DollarSign} color="#7c3aed"
          extra={editMode && baseCurrency && (
            <button onClick={addExchangeRateRow}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold text-white border-0 rounded-lg cursor-pointer"
              style={{ background: `linear-gradient(135deg, ${NAVY} 0%, ${NAVY_LIGHT} 100%)`, color: '#fff' }}>
              <Plus size={13} /> Add Rate
            </button>
          )}>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr style={{ background: `linear-gradient(135deg, ${NAVY} 0%, ${NAVY_LIGHT} 100%)` }}>
                  {['Currency', 'Exchange Rate', 'Effective Date', ...(editMode ? [''] : [])].map(h => (
                    <th key={h} className="px-4 py-3 text-left text-[11px] font-bold uppercase tracking-wider whitespace-nowrap"
                      style={{ color: '#fff' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {exchangeRates.length === 0 ? (
                  <tr>
                    <td colSpan={editMode ? 4 : 3} className="text-center py-8 text-gray-400 text-sm">
                      {baseCurrency
                        ? 'No exchange rates defined. Click "Add Rate" to add.'
                        : 'Please set a base currency in the Currencies master first.'}
                    </td>
                  </tr>
                ) : (
                  exchangeRates.map((rate, i) => (
                    <tr key={i} className="border-b border-gray-100 hover:bg-gray-50 transition-colors duration-100">
                      <td className="px-4 py-2.5">
                        {editMode ? (
                          <select value={rate.fromcurrencyid} onChange={e => updateExchangeRate(i, 'fromcurrencyid', e.target.value)}
                            className={`min-w-[180px] px-3 py-2 border-2 rounded-lg text-sm font-medium bg-white outline-none focus:border-[#0d1c6b] ${errors[`rate_currency_${i}`] ? 'border-red-500 bg-red-50' : 'border-gray-200'}`}>
                            <option value="">-- Select Currency --</option>
                            {currencies.filter(c => c.currencyid !== baseCurrency?.currencyid && c.isactive === 1).map(c => (
                              <option key={c.currencyid} value={c.currencyid}>{c.currencyname} ({c.symbol})</option>
                            ))}
                          </select>
                        ) : (
                          <span className="inline-block px-2.5 py-1 bg-blue-50 text-blue-700 border-2 border-blue-200 rounded-lg text-[12px] font-bold">
                            {getCurrencyName(rate.fromcurrencyid)}
                          </span>
                        )}
                        {errMsg(errors[`rate_currency_${i}`])}
                      </td>
                      <td className="px-4 py-2.5">
                        {editMode ? (
                          <input type="number" step="0.000001" value={rate.rate}
                            onChange={e => updateExchangeRate(i, 'rate', e.target.value)}
                            className={`min-w-[140px] px-3 py-2 border-2 rounded-lg text-sm font-medium text-right bg-amber-50 outline-none focus:border-[#0d1c6b] ${errors[`rate_value_${i}`] ? 'border-red-500' : 'border-gray-200'}`} />
                        ) : (
                          <span className="font-bold text-gray-800">{parseFloat(rate.rate || 0).toFixed(6)}</span>
                        )}
                        {errMsg(errors[`rate_value_${i}`])}
                      </td>
                      <td className="px-4 py-2.5">
                        {editMode ? (
                          <input type="date" value={rate.effectivedate}
                            onChange={e => updateExchangeRate(i, 'effectivedate', e.target.value)}
                            className={`min-w-[150px] px-3 py-2 border-2 rounded-lg text-sm font-medium bg-amber-50 outline-none focus:border-[#0d1c6b] ${errors[`rate_date_${i}`] ? 'border-red-500' : 'border-gray-200'}`} />
                        ) : (
                          new Date(rate.effectivedate).toLocaleDateString('en-GB')
                        )}
                        {errMsg(errors[`rate_date_${i}`])}
                      </td>
                      {editMode && (
                        <td className="px-4 py-2.5 text-center">
                          <button onClick={() => removeExchangeRateRow(i)} title="Remove"
                            className="w-8 h-8 flex items-center justify-center rounded-lg border-2 cursor-pointer bg-red-100 border-red-400 text-red-600 transition-all duration-150 hover:bg-red-600 hover:text-white hover:scale-110">
                            <Trash2 size={14} />
                          </button>
                        </td>
                      )}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </SectionCard>

        {/* Bottom save bar */}
        {editMode && (
          <div className="flex justify-end gap-3 print:hidden">
            <button onClick={handleSave} disabled={submitLoading}
              className="flex items-center gap-2 px-5 py-2.5 text-sm font-bold border-0 rounded-lg cursor-pointer transition-all duration-150 hover:-translate-y-0.5 disabled:opacity-60 disabled:cursor-not-allowed"
              style={{ background: `linear-gradient(135deg, ${NAVY} 0%, ${NAVY_LIGHT} 100%)`, color: '#fff' }}>
              <Save size={15} /> {submitLoading ? 'Saving…' : 'Save Changes'}
            </button>
            <button onClick={handleCancel} disabled={submitLoading}
              className="flex items-center gap-2 px-5 py-2.5 text-sm font-bold text-gray-700 bg-white border-2 border-gray-200 rounded-lg cursor-pointer transition-all duration-150 hover:bg-gray-50 disabled:opacity-60">
              <X size={15} /> Cancel
            </button>
          </div>
        )}
      </div>

      {/* Print block */}
      <div className="hidden print:block mt-8">
        <h1 className="text-center text-2xl font-bold mb-6">COMPANY SETUP</h1>
        <p><strong>Company:</strong> {companyData.companyname} ({companyData.companycode})</p>
        <p><strong>Address:</strong> {companyData.address}</p>
        <p><strong>City / Country:</strong> {companyData.city}, {companyData.country} {companyData.postal_code}</p>
        <p><strong>Phone:</strong> {companyData.phone} &nbsp;|&nbsp; <strong>Fax:</strong> {companyData.fax}</p>
        <p><strong>Email:</strong> {companyData.email} &nbsp;|&nbsp; <strong>Website:</strong> {companyData.website}</p>
        <p><strong>Tax No:</strong> {companyData.taxnumber} &nbsp;|&nbsp; <strong>Reg No:</strong> {companyData.registrationnumber}</p>
        {baseCurrency && <p><strong>Base Currency:</strong> {baseCurrency.currencyname} ({baseCurrency.symbol})</p>}
      </div>
    </div>
  );
};

export default GeneralSetup;